import React, { useEffect, useMemo, useRef } from "react";
import { graphql, useStaticQuery } from "gatsby";
import styled from "styled-components";
import sr from "@utils/sr";
import { srConfig } from "@config";
import { Icon } from "@components/icons";
import { usePrefersReducedMotion } from "@hooks";

const StyledProjectsPage = styled.main`
  width: 100%;
`;

const HeroSection = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 170px clamp(28px, 6vw, 80px) 96px;

  .eyebrow {
    margin: 0 0 18px;
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }

  h1 {
    margin: 0;
    max-width: 980px;
    color: var(--text-primary);
    font-size: clamp(48px, 7vw, 90px);
    line-height: 0.95;
    letter-spacing: -0.06em;
  }

  .lead {
    margin-top: 24px;
    max-width: 620px;
    color: var(--text-secondary);
    font-size: 18px;
    line-height: 1.7;
  }
`;

const SectionBlock = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 140px;

  .eyebrow {
    margin-bottom: 12px;
    font-size: 12px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-muted);
  }

  h2 {
    margin: 0;
    font-size: clamp(30px, 4vw, 50px);
    letter-spacing: -0.04em;
  }

  .sub {
    margin-top: 10px;
    font-size: 14px;
    color: var(--text-secondary);
  }
`;

const CategoryBlock = styled.div`
  margin-top: 60px;

  h3 {
    font-size: 26px;
    margin-bottom: 20px;
    letter-spacing: -0.02em;
  }
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.article`
  padding: 24px;
  border-radius: 20px;
  border: 1px solid var(--border-light);
  background: rgba(255, 255, 255, 0.4);
  transition: var(--transition);

  &:hover {
    transform: translateY(-4px);
    border-color: var(--border-medium);
    background: rgba(255, 255, 255, 0.7);
  }

  h4 {
    margin: 0 0 12px;
    font-size: 22px;
    letter-spacing: -0.02em;
  }

  h4 a {
    text-decoration: none;
    color: inherit;
  }

  .description {
    font-size: 14px;
    color: var(--text-secondary);
    line-height: 1.6;
  }

  .tech {
    margin-top: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .tech span {
    font-size: 11px;
    font-family: var(--font-mono);
    border: 1px solid var(--border-light);
    padding: 4px 8px;
    border-radius: 999px;
  }

  .links {
    margin-top: 14px;
    display: flex;
    gap: 8px;
  }

  .iconLink {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 1px solid var(--border-light);
  }
`;

const normalise = v => String(v || "").toLowerCase();

const inferCategory = project => {
  const text =
    normalise(project.node.frontmatter.title) +
    " " +
    normalise(project.node.frontmatter.tech?.join(" "));

  if (
    text.includes("ml") ||
    text.includes("prediction") ||
    text.includes("classification") ||
    text.includes("vqa")
  )
    return "ML / AI";

  if (
    text.includes("analysis") ||
    text.includes("revenue") ||
    text.includes("pricing")
  )
    return "Data Science";

  if (
    text.includes("dashboard") ||
    text.includes("tableau") ||
    text.includes("nasdaq")
  )
    return "Data Analyst";

  return "Software";
};

const ProjectCategory = ({ title, projects }) => {
  if (!projects.length) return null;

  return (
    <CategoryBlock>
      <h3>{title}</h3>

      <ProjectGrid>
        {projects.map(({ node }) => {
          const { title, tech, github, external } = node.frontmatter;
          const url = external || github;

          return (
            <ProjectCard key={title}>
              <h4>
                <a href={url} target="_blank" rel="noreferrer">
                  {title.replaceAll("-", " ")}
                </a>
              </h4>

              <div
                className="description"
                dangerouslySetInnerHTML={{ __html: node.html }}
              />

              <div className="tech">
                {tech?.map((t, i) => (
                  <span key={i}>{t}</span>
                ))}
              </div>

              <div className="links">
                {github && (
                  <a className="iconLink" href={github}>
                    <Icon name="GitHub" />
                  </a>
                )}
                {external && (
                  <a className="iconLink" href={external}>
                    <Icon name="External" />
                  </a>
                )}
              </div>
            </ProjectCard>
          );
        })}
      </ProjectGrid>
    </CategoryBlock>
  );
};

const ProjectsPageContent = () => {
  const data = useStaticQuery(graphql`
    query {
      projects: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/projects/" }
          frontmatter: { showInProjects: { ne: false } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              tech
              github
              external
            }
            html
          }
        }
      }
    }
  `);

  const prefersReducedMotion = usePrefersReducedMotion();
  const ref = useRef(null);

  useEffect(() => {
    if (!prefersReducedMotion && ref.current) {
      sr.reveal(ref.current, srConfig());
    }
  }, [prefersReducedMotion]);

  const grouped = useMemo(() => {
    const groups = {
      "ML / AI": [],
      "Data Science": [],
      "Data Analyst": [],
      Software: [],
    };

    data.projects.edges.forEach(p => {
      const cat = inferCategory(p);
      groups[cat].push(p);
    });

    return groups;
  }, [data]);

  return (
    <StyledProjectsPage ref={ref}>
      <HeroSection>
        <p className="eyebrow">Projects</p>
        <h1>Systems for data and intelligence.</h1>
        <p className="lead">
          Selected work across machine learning, analytics, and software.
        </p>
      </HeroSection>

      <SectionBlock>
        <p className="eyebrow">Work</p>
        <h2>Selected projects</h2>
        <p className="sub">Grouped by capability</p>

        <ProjectCategory title="ML / AI" projects={grouped["ML / AI"]} />
        <ProjectCategory title="Data Science" projects={grouped["Data Science"]} />
        <ProjectCategory title="Data Analyst" projects={grouped["Data Analyst"]} />
        <ProjectCategory title="Software" projects={grouped["Software"]} />
      </SectionBlock>
    </StyledProjectsPage>
  );
};

export default ProjectsPageContent;